// Approve / Decline / Undo for a single step. Pass `inline` to hide the
// "you can't do this" messages — the inbox row uses that so only real
// actions show at the top of the list.

import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { approveStep, getApprovalStep, unapproveStep, rejectStep } from "./actions";
import { getApprovals } from "modules/approval/actions";
import { getCurrentUser } from "modules/current_user/actions";
import { f7, Button as MobileButton, Popup, Page, NavRight, Block, Link, Navbar } from "framework7-react";

const ACTION_COPY = {
  approve: {
    title: "Approve",
    body: "Approve this item?",
    confirm: "Approve",
    color: "success",
    mobileColor: "green",
    toast: "Approved",
  },
  reject: {
    title: "Decline",
    body: "Decline this item? This stops the whole approval, not just this step.",
    confirm: "Decline",
    color: "danger",
    mobileColor: "red",
    toast: "Declined",
  },
  unapprove: {
    title: "Undo approval",
    body: "Unapprove this step?",
    confirm: "Undo",
    color: "warning",
    mobileColor: "orange",
    toast: "Unapproved",
  },
};

function mapStateToProps(state) {
  return {
    approval_steps: state.approval_steps,
    current_user: state.current_user,
  };
}

const ApprovalStepButton = (props) => {
  const { approval_step_id, approval_steps, current_user, approval, mobile, inline } = props;
  const approval_step = approval_steps[approval_step_id] || props.approval_step;
  const [pendingAction, setPendingAction] = useState(null);
  const toggle = () => setPendingAction(null);

  const LabelElement = mobile ? Block : "span";

  useEffect(() => {
    if (!approval_step && approval_step_id) {
      props.getApprovalStep(approval_step_id);
    }
  }, []);

  if (!approval_step) return null;

  if (_.includes(approval_step.user_ids, current_user.id) === false) {
    if (inline) return null;
    return (
      <LabelElement>
        <b>Only {approval_step.approver || approval_step.users} can approve this.</b>
      </LabelElement>
    );
  }

  let approvable = true;
  if (approval?.steps) {
    const previous_steps = approval.steps.filter((s) => s.step_number < approval_step.step_number);
    if (previous_steps.length > 0) {
      approvable = _.every(previous_steps, (s) => s.approved === true);
    }
  }
  if (!approvable) {
    if (inline) return null;
    return (
      <LabelElement>
        <b>Requires previous step/s to be approved first.</b>
      </LabelElement>
    );
  }

  let unapprovable = approval_step.approved;
  if (approval?.steps) {
    const subsequent_steps = approval.steps.filter((s) => s.step_number > approval_step.step_number);
    if (subsequent_steps.length > 0) {
      unapprovable = _.every(subsequent_steps, (s) => s.approved === false);
    }
  }
  if (approval_step.approved && !unapprovable) {
    if (inline) return null;
    return (
      <LabelElement>
        <b>Requires subsequent step/s to be unapproved first.</b>
      </LabelElement>
    );
  }

  const viewProps = {
    ...props,
    pendingAction,
    setPendingAction,
    toggle,
    approval_step_id,
    approval_step,
  };

  return mobile ? <MobileView {...viewProps} /> : <WebView {...viewProps} />;
};

export default connect(mapStateToProps, {
  getApprovalStep,
  approveStep,
  unapproveStep,
  rejectStep,
  getApprovals,
  getCurrentUser,
})(ApprovalStepButton);

const runAction = (props, action, approval_step_id) => {
  const fns = { approve: props.approveStep, reject: props.rejectStep, unapprove: props.unapproveStep };
  return fns[action](approval_step_id)
    .then(() => {
      props.getApprovals();
      props.getCurrentUser();
      toastr.success(ACTION_COPY[action].toast);
    })
    .catch((err) => {
      const msg = err?.response?.data?.error || "Action failed";
      toastr.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    });
};

const WebView = (props) => {
  const { pendingAction, setPendingAction, toggle, approval_step, approval_step_id } = props;
  const copy = ACTION_COPY[pendingAction] || ACTION_COPY.approve;
  const approved = approval_step?.approved;

  return (
    <Fragment>
      <div className="approval-actions" onClick={(e) => e.stopPropagation()}>
        {approved ? (
          <Button size="sm" color="secondary" outline onClick={() => setPendingAction("unapprove")}>
            Undo
          </Button>
        ) : (
          <Fragment>
            <Button size="sm" color="success" onClick={() => setPendingAction("approve")}>
              Approve
            </Button>
            <Button size="sm" color="danger" outline className="ml-1" onClick={() => setPendingAction("reject")}>
              Decline
            </Button>
          </Fragment>
        )}
      </div>

      <Modal isOpen={!!pendingAction} toggle={toggle}>
        <ModalHeader toggle={toggle}>{copy.title}</ModalHeader>
        <ModalBody>{copy.body}</ModalBody>
        <ModalFooter>
          <Button
            color={copy.color}
            onClick={() => {
              runAction(props, pendingAction, approval_step_id).finally(() => setPendingAction(null));
            }}
          >
            {copy.confirm}
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

const MobileView = (props) => {
  const { approval_step, pendingAction, setPendingAction, approval_step_id } = props;
  const copy = ACTION_COPY[pendingAction] || ACTION_COPY.approve;
  const approved = approval_step?.approved;
  const popupId = `approval-step-button-${approval_step_id}`;

  return (
    <Fragment>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {approved ? (
          <MobileButton small fill color="gray" onClick={() => setPendingAction("unapprove")}>
            Undo
          </MobileButton>
        ) : (
          <Fragment>
            <MobileButton small fill color="green" onClick={() => setPendingAction("approve")}>
              Approve
            </MobileButton>
            <MobileButton small fill color="red" onClick={() => setPendingAction("reject")}>
              Decline
            </MobileButton>
          </Fragment>
        )}
      </div>

      <Popup opened={!!pendingAction} id={popupId} onPopupClosed={() => setPendingAction(null)}>
        <Page>
          <Navbar title={copy.title}>
            <NavRight>
              <Link popupClose>Close</Link>
            </NavRight>
          </Navbar>
          <Block>{copy.body}</Block>
          <Block>
            <MobileButton
              fill
              color={copy.mobileColor}
              className="margin"
              onClick={() => {
                runAction(props, pendingAction, approval_step_id).finally(() => {
                  f7.popup.close(`#${popupId}`);
                });
              }}
            >
              {copy.confirm}
            </MobileButton>
            <MobileButton color="gray" className="margin" fill onClick={() => f7.popup.close(`#${popupId}`)}>
              Cancel
            </MobileButton>
          </Block>
        </Page>
      </Popup>
    </Fragment>
  );
};
